import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import './ChecklistView.css';

// Simple Icon Components
const CheckIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>;
const CloseIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>;

const ChecklistView = () => {
    const [checklistHoy, setChecklistHoy] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generando, setGenerando] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            setLoading(true);
            const templatesRes = await axios.get('/api/checklist/templates');
            setTemplates(templatesRes.data);

            try {
                const hoyRes = await axios.get('/api/checklist/daily/today');
                setChecklistHoy(hoyRes.data);
            } catch (error) {
                if (error.response?.status === 404) {
                    setChecklistHoy(null);
                }
            }
        } catch (error) {
            console.error('Error al cargar datos:', error);
        } finally {
            setLoading(false);
        }
    };

    const generarChecklistHoy = async () => {
        if (templates.length === 0) {
            alert('No hay templates disponibles');
            return;
        }

        const userInfoStr = localStorage.getItem('userInfo');
        const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
        const usuarioId = userInfo ? userInfo.id : null;

        if (!usuarioId) {
            alert('Error: No se pudo identificar al usuario actual. Por favor inicie sesión nuevamente.');
            return;
        }

        try {
            setGenerando(true);
            const response = await axios.post('/api/checklist/daily/generate', {
                template_id: templates[0].id,
                fecha: format(new Date(), 'yyyy-MM-dd'),
                usuario_id: usuarioId
            });
            setChecklistHoy(response.data);
        } catch (error) {
            console.error('Error al generar checklist:', error);
            alert(error.response?.data?.error || 'Error al generar checklist');
        } finally {
            setGenerando(false);
        }
    };

    const marcarItem = async (itemId, estado, notas = '') => {
        try {
            const response = await axios.post(
                `/api/checklist/daily/${checklistHoy.id}/items/${itemId}/check`,
                {
                    estado,
                    notas,
                    crear_evento: estado === 'fallido'
                }
            );
            setChecklistHoy(response.data);
        } catch (error) {
            console.error('Error al marcar item:', error);
            alert('Error al actualizar item');
        }
    };

    const completarChecklist = async () => {
        if (!window.confirm('¿Está seguro de completar el checklist?')) {
            return;
        }

        try {
            const response = await axios.post(
                `/api/checklist/daily/${checklistHoy.id}/complete`,
                {
                    observaciones: 'Checklist completado'
                }
            );
            setChecklistHoy(response.data);
            alert('Checklist completado exitosamente');
        } catch (error) {
            console.error('Error al completar checklist:', error);
            alert('Error al completar checklist');
        }
    };

    const reiniciarChecklist = async () => {
        if (!window.confirm('⚠️ ADVERTENCIA: Esto reiniciará el checklist completo y perderá todo el progreso. ¿Está seguro?')) {
            return;
        }

        try {
            const response = await axios.post(
                `/api/checklist/daily/${checklistHoy.id}/reset`
            );
            setChecklistHoy(response.data);
            alert('Checklist reiniciado exitosamente');
        } catch (error) {
            console.error('Error al reiniciar checklist:', error);
            alert('Error al reiniciar checklist');
        }
    };

    if (loading) {
        return <div className="loading-state">Cargando checklist...</div>;
    }

    if (!checklistHoy) {
        return (
            <div className="checklist-view">
                <div className="empty-state">
                    <h2>Checklist Diario</h2>
                    <p>No se ha iniciado el checklist de hoy.</p>
                    <button
                        className="btn-primary"
                        onClick={generarChecklistHoy}
                        disabled={generando}
                        style={{ marginTop: '16px' }}
                    >
                        {generando ? 'Iniciando...' : 'Iniciar Ahora'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="checklist-view">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                <div className="flex flex-col gap-1">
                    <h2 className="page-title">Checklist Diario</h2>
                    <span className="text-sm font-medium text-gray-500 capitalize">
                        {format(new Date(), "EEEE, d 'de' MMMM", { locale: es })}
                    </span>
                </div>
                <div className="flex gap-3 items-center">
                    <button
                        className="btn-secondary"
                        onClick={reiniciarChecklist}
                        title="Reiniciar progreso"
                    >
                        Reiniciar
                    </button>
                </div>
            </div>

            <div className="checklist-container">
                {/* Progress Minimal */}
                <div className="progress-card">
                    <div className="progress-info-row">
                        <span className="progress-text">
                            Progreso ({checklistHoy.items_completados}/{checklistHoy.items_total})
                        </span>
                        <span className="progress-text" style={{ color: '#0033a0' }}>{checklistHoy.porcentaje_completado}%</span>
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${checklistHoy.porcentaje_completado}%` }}
                        />
                    </div>
                </div>

                {/* Items List */}
                <div className="checklist-items-list">
                    {checklistHoy.items && [...checklistHoy.items].sort((a, b) => a.id - b.id).map((item, index) => (
                        <ChecklistItemCard
                            key={item.id}
                            item={item}
                            onMarcar={marcarItem}
                        />
                    ))}
                </div>

                {checklistHoy.estado === 'completado' && (
                    <div className="estado-completado" style={{ textAlign: 'center', color: '#059669', fontWeight: '600', padding: '20px' }}>
                        ✓ Completado a las {format(new Date(checklistHoy.hora_fin), "hh:mm a", { locale: es })}
                    </div>
                )}
            </div>
        </div>
    );
};

const ChecklistItemCard = ({ item, onMarcar }) => {
    const [showNotes, setShowNotes] = useState(false);
    const [notes, setNotes] = useState(item.notas || '');

    // Service status from backend
    const [serviceStatus, setServiceStatus] = useState({
        status: 'checking', // 'checking', 'online', 'offline', 'degraded'
        responseTime: null,
        lastCheck: null
    });

    // Determine which service to check based on item description
    const getServiceToCheck = () => {
        const desc = item.descripcion.toLowerCase();

        // Detectar verificaciones de conectividad por sede
        if (desc.includes('internet') || desc.includes('conectividad')) {
            if (desc.includes('ca1')) return { type: 'connectivity', sede: 'CA1' };
            if (desc.includes('cdn')) return { type: 'connectivity', sede: 'CDN' };
            if (desc.includes('ccs')) return { type: 'connectivity', sede: 'CCS' };
            // Si solo dice "internet" sin especificar sede, verificar todas
            return { type: 'connectivity', sede: 'all' };
        }

        // Detectar verificación de Profit (SQL Server)
        if (desc.includes('profit') || desc.includes('sql')) {
            return { type: 'sqlserver', host: '192.168.141.30', port: 1433 };
        }

        // Detectar verificación de enlace entre sedes
        if (desc.includes('enlace') && desc.includes('sedes')) {
            return { type: 'intersite' };
        }

        return null; // No es un servicio verificable automáticamente
    };

    // Real status check
    React.useEffect(() => {
        const checkService = async () => {
            const serviceToCheck = getServiceToCheck();

            if (!serviceToCheck) {
                // No hay verificación automática para este item
                setServiceStatus({
                    status: 'unknown',
                    responseTime: null,
                    lastCheck: null
                });
                return;
            }

            try {
                if (serviceToCheck.type === 'connectivity') {
                    let response;

                    if (serviceToCheck.sede === 'all') {
                        // Verificar todas las sedes
                        response = await axios.get('/api/checklist/verify/connectivity');
                        const sedes = response.data.sedes;

                        // Determinar estado general
                        const allOnline = Object.values(sedes).every(s => s.status === 'online');
                        const someOnline = Object.values(sedes).some(s => s.status === 'online');
                        const avgResponseTime = Object.values(sedes).reduce((acc, s) => acc + (s.response_time || 0), 0) / Object.keys(sedes).length;

                        setServiceStatus({
                            status: allOnline ? 'online' : (someOnline ? 'degraded' : 'offline'),
                            responseTime: allOnline ? Math.round(avgResponseTime) : null,
                            lastCheck: new Date()
                        });
                    } else {
                        // Verificar sede específica
                        response = await axios.get(`/api/checklist/verify/connectivity/${serviceToCheck.sede}`);

                        setServiceStatus({
                            status: response.data.status,
                            responseTime: response.data.response_time ? Math.round(response.data.response_time) : null,
                            lastCheck: new Date()
                        });
                    }
                } else if (serviceToCheck.type === 'sqlserver') {
                    // Verificar SQL Server
                    const response = await axios.get(`/api/checklist/verify/sqlserver?host=${serviceToCheck.host}&port=${serviceToCheck.port}`);

                    setServiceStatus({
                        status: response.data.status,
                        responseTime: response.data.response_time ? Math.round(response.data.response_time) : null,
                        lastCheck: new Date()
                    });
                } else if (serviceToCheck.type === 'intersite') {
                    // Verificar enlace entre sedes
                    const response = await axios.get('/api/checklist/verify/intersite');

                    setServiceStatus({
                        status: response.data.status,
                        responseTime: response.data.response_time ? Math.round(response.data.response_time) : null,
                        lastCheck: new Date()
                    });
                }
            } catch (error) {
                console.error('Error verificando servicio:', error);
                setServiceStatus({
                    status: 'error',
                    responseTime: null,
                    lastCheck: new Date(),
                    error: error.message
                });
            }
        };

        checkService();

        // Verificar cada 30 segundos
        const interval = setInterval(checkService, 30000);
        return () => clearInterval(interval);
    }, [item.descripcion]);

    const handleCheck = (status) => {
        if (status === 'fallido') {
            setShowNotes(true);
        } else {
            onMarcar(item.id, 'ok');
            setShowNotes(false);
        }
    };

    const saveFailure = () => {
        onMarcar(item.id, 'fallido', notes);
        setShowNotes(false);
    };

    const cancelFailure = () => {
        setShowNotes(false);
        setNotes(item.notas || '');
    };

    const getStatusDisplay = () => {
        switch (serviceStatus.status) {
            case 'checking':
                return { text: 'Verificando...', class: 'status-checking' };
            case 'online':
                return {
                    text: `Online ${serviceStatus.responseTime ? `• ${serviceStatus.responseTime}ms` : ''}`,
                    class: 'status-online'
                };
            case 'offline':
                return { text: 'Sin respuesta', class: 'status-offline' };
            case 'degraded':
                return { text: 'Degradado', class: 'status-degraded' };
            case 'error':
                return { text: 'Error', class: 'status-offline' };
            case 'unknown':
                return { text: 'N/A', class: 'status-unknown' };
            default:
                return { text: 'Desconocido', class: 'status-unknown' };
        }
    };

    const statusDisplay = getStatusDisplay();

    return (
        <>
            <div className={`checklist-item-card status-${item.estado}`}>
                {/* Column 1: Description + Status Indicator */}
                <div className="item-content">
                    <div className="item-description">
                        {item.descripcion}
                        {item.notas && !showNotes && (
                            <span style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600, marginLeft: '8px' }}>
                                • Reportado
                            </span>
                        )}
                    </div>
                </div>

                {/* Column 2: Service Status Panel */}
                <div className={`service-status-panel ${statusDisplay.class}`}>
                    <span className="status-indicator"></span>
                    <span className="status-text">{statusDisplay.text}</span>
                </div>

                {/* Column 3: Action Buttons */}
                {item.estado !== 'completado' && (
                    <div className="item-actions">
                        <button
                            className={`btn-check action-ok ${item.estado === 'ok' ? 'active-ok' : ''}`}
                            onClick={() => handleCheck('ok')}
                            title="Operativo"
                        >
                            <CheckIcon />
                        </button>
                        <button
                            className={`btn-check action-fail ${item.estado === 'fallido' ? 'active-fail' : ''}`}
                            onClick={() => handleCheck('fallido')}
                            title="Fallo"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                )}
            </div>

            {showNotes && (
                <div className="item-notes-panel">
                    <textarea
                        className="notes-input"
                        rows="2"
                        placeholder="Descripción de la falla..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        autoFocus
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', marginBottom: '8px' }}
                    />
                    <div className="notes-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button className="btn-reset" onClick={cancelFailure} style={{ fontSize: '0.8rem', padding: '4px 12px' }}>Cancelar</button>
                        <button className="btn-completar" style={{ backgroundColor: '#dc2626', fontSize: '0.8rem', padding: '4px 12px' }} onClick={saveFailure}>
                            Registrar
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChecklistView;